import type {
  Forloeb,
  ForloebPunkt,
  Krydsning,
  Profil,
  Produktversion,
  Resultat,
  Satser,
} from "./types";

/**
 * Expected annual consumption in kWh.
 * kwhOverride wins when set; otherwise km × Wh/km ÷ 1000, rounded.
 */
export function kwhYear(profil: Pick<Profil, "kmAar" | "whKm" | "kwhOverride">): number {
  if (profil.kwhOverride != null) return Math.round(profil.kwhOverride);
  return Math.round((profil.kmAar * profil.whKm) / 1000);
}

/** Round half away from zero — must match C# MidpointRounding.AwayFromZero. */
function roundAway(n: number): number {
  return Math.sign(n) * Math.round(Math.abs(n));
}

/**
 * The one-off cost of getting started on a deal, kr — what lands at year 0.
 *
 * A box only costs extra when the deal doesn't bundle one and the user doesn't
 * already own one. The signup/installation fee is due either way: a bundled box
 * is a *rented* box, and it still has to be mounted, so owning a box of your own
 * doesn't get you out of it.
 *
 * {@link annualCost} amortises this over {@link STANDARD_HORISONT_AAR} years;
 * {@link forloeb} charges it in full at year 0. Both read it from here so the
 * two views can never drift apart.
 */
export function engangsOmkostning(
  profil: Pick<Profil, "harEgenBoks">,
  pv: Pick<Produktversion, "ladeboksInkluderet" | "boksPris" | "oprettelsesGebyr">,
): number {
  const boks = !pv.ladeboksInkluderet && !profil.harEgenBoks ? pv.boksPris : 0;
  return boks + (pv.oprettelsesGebyr ?? 0);
}

/**
 * Annual total cost for one product version against one profile, given the
 * resolved (dated) global rates. Pure: identical to the C# PriceEngine and
 * validated by shared/test-vectors/cases.json.
 *
 * See docs/02_BEREGNINGSMODEL.md §1–§4.
 */
export function annualCost(profil: Profil, pv: Produktversion, satser: Satser): Resultat {
  const kwh = kwhYear(profil);
  const udeProcent = profil.udeProcent ?? 0;
  const kwhUde = (kwh * udeProcent) / 100;
  const kwhHome = kwh - kwhUde;

  const net = profil.laderOmNatten ? satser.standardNettarifNat : satser.standardNettarif;
  const effRefusion = Math.max(0, satser.lovligRefusionssats * pv.refusionAndel - pv.refusionGebyr);
  const betingetTillaeg =
    pv.tillaegTaerskel != null ? Math.max(0, satser.spotpris - pv.tillaegTaerskel) : 0;
  const elBase =
    pv.prismodel === "spot" ? satser.spotpris + pv.kwhTillaeg + betingetTillaeg : pv.fastprisKrKwh;
  const homePrice = elBase + net + satser.elafgift - effRefusion;
  const udePrice = pv.udeprisSomHjemme ? homePrice : pv.udeprisKrKwh;

  const install = engangsOmkostning(profil, pv) / STANDARD_HORISONT_AAR;

  const total = kwhHome * homePrice + kwhUde * udePrice + pv.maanedsabonnement * 12 + install;

  return {
    total: roundAway(total),
    homePrice,
    udePrice,
    effRefusion,
    install: roundAway(install),
    kwh,
    kwhHome,
    kwhUde,
  };
}

/** Default comparison horizon in years — matches the 5-year box amortisation. */
export const STANDARD_HORISONT_AAR = 5;

/** Default sampling resolution for a cost curve: one point per month. */
export const MAANEDER_PR_AAR = 12;

/**
 * Cost over time for one product version — the basis of the comparison graph.
 *
 * Where {@link annualCost} spreads a charging box over five years so every row is
 * a comparable kr/year figure, this models what the customer actually pays out:
 * the box lands in full at year 0 and only the running costs accrue after that.
 * That is what makes the curves cross — an agreement with a bundled box starts
 * higher on the y-axis but climbs slower, and the crossing is the break-even.
 *
 * `aarlig` is derived as `total − install` from the same {@link annualCost} call
 * the result list shows, so the two views always reconcile to the krone.
 *
 * Rates are held constant across the horizon (see docs/02_BEREGNINGSMODEL.md) —
 * we project today's dated rate row forward rather than guessing at price
 * developments. Pure: no dates, no I/O.
 *
 * Sampled monthly by default: the running cost accrues month by month, so the
 * curve should say what has actually been paid out at any given month rather
 * than only at each anniversary.
 */
export function forloeb(
  profil: Profil,
  pv: Produktversion,
  satser: Satser,
  aar: number = STANDARD_HORISONT_AAR,
  punkterPrAar: number = MAANEDER_PR_AAR,
): Forloeb {
  const r = annualCost(profil, pv, satser);
  const etablering = roundAway(engangsOmkostning(profil, pv));
  const aarlig = r.total - r.install;

  const punkter: ForloebPunkt[] = [];
  const antal = Math.round(aar * punkterPrAar);
  for (let i = 0; i <= antal; i++) {
    const tid = i / punkterPrAar;
    punkter.push({ aar: tid, akkumuleret: roundAway(etablering + aarlig * tid) });
  }
  return { etablering, aarlig, aar, punkter, aarligMedAmortisering: r.total };
}

/** Accumulated cost at an arbitrary point in time (years, may be fractional). */
export function akkumuleretVed(f: Forloeb, tidAar: number): number {
  return f.etablering + f.aarlig * Math.max(0, tidAar);
}

/**
 * Break-even points between two cost curves within the horizon.
 *
 * Both curves are straight lines after the year-0 step, so a crossing is exact
 * rather than interpolated: t* = Δetablering / Δårlig. Returned only when it
 * falls strictly inside the horizon — two curves that never cross, or cross
 * only beyond it, have no useful break-even to show.
 */
export function krydsning(a: Forloeb, b: Forloeb, aar: number = STANDARD_HORISONT_AAR): Krydsning | null {
  const dAarlig = b.aarlig - a.aarlig;
  if (dAarlig === 0) return null; // parallel — the cheaper one stays cheaper
  const t = (a.etablering - b.etablering) / dAarlig;
  if (!(t > 0 && t <= aar)) return null;
  return { tidAar: t, belob: roundAway(akkumuleretVed(a, t)) };
}
